import { useCallback, useMemo, useContext, useState } from "react";
import { Marker, Popup } from "react-leaflet";
import L, { LatLngExpression, LeafletEventHandlerFnMap } from "leaflet";
import tinycolor from 'tinycolor2';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShareNodes, faRoute, faQrcode } from "@fortawesome/pro-solid-svg-icons";

import eventBus from "@/eventBus";
import { LeafletContext } from "@/App";
import { interactiveURL } from "@/config";
import { LeafletIcon } from "@/components/markers/LeafletIcon";
import { QrCodeModal } from '@/components/Map/PathOverlay/QrCodeModal';

import {
  activeImageSelector,
  isEditingSelector,
  isSelectingWaypointsSelector,
  mapSelector,
  markersSelector,
  mapMarkersSelector,
  setActiveSpace,
  SpaceType,
  toggleSelectedWaypoint,
  useAppDispatch,
  useAppSelector,
  // Navigation
  PathNode,
  hasPathsOnCurrentLayerSelector,
  setCustomRouteStart,
  setCustomRouteEnd,
  setShowCustomRoute,
  spaceNumbersSelector,
  spaceLettersSelector,
  preferencesSelector,
} from "../../../../store";

// Invisible anchor of spaces shown as overlays, opening the space details when clicked
const overlaySpaceAnchorIcon = L.divIcon({ className: 'overlay-space-anchor', html: '', iconSize: [1, 1], iconAnchor: [0, 0] });

export function Space({ space, position, eventHandlers, openModal, setActive, connectedSpaces = [], findNodeBySpaceId, hiddenMarker = false }:
  { space: SpaceType; position: LatLngExpression; eventHandlers?: LeafletEventHandlerFnMap; openModal?: Function; setActive?: (space: SpaceType) => void; connectedSpaces?: SpaceType[]; findNodeBySpaceId?: (spaceId: string) => PathNode | undefined; hiddenMarker?: boolean }) {
  const { activeMarkerRef, mapRef, hoveredMarker, setHoveredMarker } = useContext(LeafletContext);

  const dispatch = useAppDispatch();
  const iMap = useAppSelector(mapSelector);
  const isSelectingWaypoints = useAppSelector(isSelectingWaypointsSelector);
  const activeImage = useAppSelector(activeImageSelector);
  const isEditing = useAppSelector(isEditingSelector);
  const selectedWaypointIds = useAppSelector((s) => s.wayfinding.selectedWaypointIds);
  const hasIndoorPaths = useAppSelector(hasPathsOnCurrentLayerSelector);
  const number = useAppSelector(spaceNumbersSelector)[space.id];
  const letter = useAppSelector(spaceLettersSelector)[space.id];
  const preferences = useAppSelector(preferencesSelector);

  const markers = useAppSelector(markersSelector);
  const defaultMarkers = useAppSelector(mapMarkersSelector);

  const [showQrModal, setShowQrModal] = useState(false);

  const marker = useMemo(() => {
    return markers.find((m) => m.id === space.markerId) ?? defaultMarkers;
  }, [markers, defaultMarkers, space.markerId]);

  const icons = useMemo(() => {
    const scale = 1.25; // Hover scale factor
    const [width, height] = marker.size.split('x').map(Number);
    const iconParams = {
      marker,
      number,
      letter,
      width,
      height,
      className: 'only-light',
    };
    return {
      regular: LeafletIcon(iconParams),
      hovered: LeafletIcon({ ...iconParams, scale }),
      transparent: LeafletIcon({ ...iconParams, className: 'only-light transparent' }),
      selected: LeafletIcon({ ...iconParams, className: 'waypoint-selected' }),
      selectedBig: LeafletIcon({ ...iconParams, scale, className: 'waypoint-selected' }),
    };
  }, [marker, number, letter]);

  const getSpaceIcon = useCallback((space: SpaceType) => {
    const isSelected = selectedWaypointIds.includes(space.id);
    const isHovered = hoveredMarker === space.id;
    const isOtherHovered = hoveredMarker && hoveredMarker !== space.id;

    if (!icons) return;

    // Show blue selection styling for any selected space, regardless of selection mode
    if (isSelected) {
      return isHovered ? icons.selectedBig : icons.selected;
    }

    if (isHovered) {
      return icons.hovered;
    }

    if (isOtherHovered) {
      return icons.transparent;
    }
    return icons.regular;
  }, [icons, hoveredMarker, selectedWaypointIds]);

  const spaceLink = `${interactiveURL}/${iMap.order.id}/map/${iMap.id}/layer/${activeImage?.id}/space/${space.id}`;

  const copyLinkToClipboard = useCallback(() => {
    navigator.clipboard.writeText(spaceLink);
    eventBus.emit('flash', 'success', 'Copied URL to clipboard!');
  }, [spaceLink]);

  const icon = getSpaceIcon(space);
  if (!icon) {
    return null;
  }

  // Navigate from one space to another
  const handleNavigateTo = (fromSpace: SpaceType, toSpaceId: string) => {
    const marker = activeMarkerRef.current[fromSpace.id];
    if (marker) marker.closePopup();
    dispatch(setCustomRouteStart(fromSpace.id));
    dispatch(setCustomRouteEnd(toSpaceId));
    dispatch(setShowCustomRoute(true));
  };

  return (
    <>
      <Marker
        // Re-created when switching between pin and overlay: `interactive` only applies on creation
        key={hiddenMarker ? 'overlay' : 'pin'}
        icon={hiddenMarker ? overlaySpaceAnchorIcon : icon}
        title={space.name}
        ref={(ref) => (activeMarkerRef.current[space.id] = ref!)}
        position={position}
        interactive={!hiddenMarker}
        draggable={isEditing && !hiddenMarker}
        autoPan={true}
        eventHandlers={{
          click: (e) => {
            // Handle waypoint selection - allow selecting ANY space, not just marked waypoints
            if (isSelectingWaypoints) {
              e.target.closePopup();
              dispatch(toggleSelectedWaypoint(space.id));
            } else if (hiddenMarker && !isEditing) {
              setActive?.(space);
              openModal?.();
            }
            // For card popup style, set active space on click
            if (preferences.popupStyle === 'card') {
              dispatch(setActiveSpace(space.id));
            }
          },

          // Zoom marker on mouse over but not while dragging
          // since it causes problems when draggin over another marker
          mouseover: () => setHoveredMarker(space.id),
          mouseout: () => setHoveredMarker(''),
          popupopen: (e) => {
            dispatch(setActiveSpace(space.id));
            e.popup.options.autoPan = true;
            // Auto zoom to max level for better visibility (if enabled for this space)
            if (space.auto_zoom && mapRef.current) {
              mapRef.current.flyTo(
                e.target.getLatLng(),
                mapRef.current.options.maxZoom,
              );
              // Re-center popup after flying to marker
              mapRef.current.once(
                'zoomend',
                () => mapRef.current!.fire('popupopen', {popup: e.popup })
              );
            }
            requestAnimationFrame(() => {
              // Disable autoPan after Popup is centered
              // fixes moving Map when hovering markers
              e.popup.options.autoPan = false;
            });
          },
          popupclose: (e) => {
            dispatch(setActiveSpace(null));
            e.popup.options.autoPan = true;
          },
          ...eventHandlers,
        }}
      >
        {preferences.popupStyle !== 'card' && !hiddenMarker && (
          <Popup autoPan={true}>
            <div className="explore-card">
              {space.visual || space.visual_uploaded?.base64 ? (
                <div className="explore-card__media">
                  {!!(space.vr_iframe || space.vr_model || space.video || space.carousel_images?.length) ? (
                    <button
                      className="tour-icon"
                      onClick={() => {
                        setActive?.(space);
                        openModal?.();
                      }}
                    >
                      View
                    </button>
                  ) : (
                      <span></span>
                    )}
                  <img
                    src={space.visual || space.visual_uploaded.base64}
                    alt={space.name}
                    onError={(i) => ((i.target as HTMLImageElement).style.display = 'none')}
                  />
                </div>
              ) : (
                  <span></span>
                )}
              <div className="explore-card__menu">
                <FontAwesomeIcon
                  icon={faQrcode}
                  size="xl"
                  style={{ color: tinycolor.isReadable("#FFF", iMap.secondaryColor, {}) ? '' : '#666' }}
                  className="explore-card__qr"
                  onClick={() => setShowQrModal(true)}
                />
                <FontAwesomeIcon
                  icon={faShareNodes}
                  size="xl"
                  style={{ color: tinycolor.isReadable("#FFF", iMap.secondaryColor, {}) ? '' : '#666' }}
                  className="explore-card__share"
                  onClick={() => copyLinkToClipboard()}
                />
                <h2 className="explore-card__title">{space.name}</h2>
                {space.subtitle && (
                  <p className="explore-card__subtitle">
                    {space.subtitle}
                  </p>
                )}
              </div>
              {space.external_link && !(space.vr_iframe || space.vr_model || space.video || space.carousel_images?.length) && (
                <a
                  href={space.external_link}
                  target="_blank"
                  rel="noreferrer"
                >
                  <button className="external-link-button">Learn More</button>
                </a>
              )}
              {/* Navigation section - only show if paths exist on this layer */}
              {hasIndoorPaths && !isEditing && findNodeBySpaceId && (() => {
                const showNavigation = preferences.wayfindingDropdowns
                  && findNodeBySpaceId(space.id)
                  && connectedSpaces.length > 0;

                // Only show if this space is part of the path network and has connections
                if (!showNavigation) return null;

                return (
                  <div className="explore-card__navigation">
                    <label className="navigate-label">
                      <FontAwesomeIcon icon={faRoute} />
                      Navigate to:
                    </label>
                    <select
                      className="navigate-select"
                      defaultValue=""
                      onChange={(e) => {
                        if (e.target.value) {
                          handleNavigateTo(space, e.target.value);
                        }
                      }}
                    >
                      <option value="" disabled>Select destination...</option>
                      {connectedSpaces.map((connSpace) => (
                        <option key={connSpace.id} value={connSpace.id}>
                          {connSpace.name}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })()}
            </div>
          </Popup>
        )}
      </Marker>
      {showQrModal && (
        <QrCodeModal
          waypointUrl={spaceLink}
          waypointName={space.name}
          onClose={() => setShowQrModal(false)}
        />
      )}
    </>
  );
};
